// Route Utilities
// This file contains utility functions for route management

import { 
  ROUTE_PATHS, 
  ROUTE_METADATA, 
  ROUTE_PERMISSIONS, 
  ROUTE_REDIRECTS,
  ROUTE_ERROR_MESSAGES,
  ROUTE_SUCCESS_MESSAGES,
  ROUTE_LOADING_STATES,
  ROUTE_CACHE_CONFIG,
  ROUTE_ANALYTICS_CONFIG
} from '../config/routeConfig';

// Route Meta Update
export const updateRouteMeta = (path) => {
  try {
    const metadata = ROUTE_METADATA[path];
    
    if (!metadata) {
      return;
    }
    
    // Update document title
    document.title = metadata.title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', metadata.description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = metadata.description;
      document.head.appendChild(metaDescription);
    }
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', metadata.keywords);
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      metaKeywords.content = metadata.keywords;
      document.head.appendChild(metaKeywords);
    }
    
  } catch (error) {
    console.error('Route meta update error:', error);
  }
};

// Route Validation
export const validateRoute = (path, user = null, subscription = null) => {
  try {
    // Check if route exists
    if (!ROUTE_METADATA[path]) {
      return {
        valid: false,
        error: ROUTE_ERROR_MESSAGES.INVALID_ROUTE,
        code: 'INVALID_ROUTE'
      };
    }
    
    const metadata = ROUTE_METADATA[path];
    
    // Check authentication requirement
    if (metadata.requiresAuth && !user) {
      return {
        valid: false,
        error: ROUTE_ERROR_MESSAGES.AUTHENTICATION_REQUIRED,
        code: 'AUTHENTICATION_REQUIRED',
        redirectTo: ROUTE_PATHS.PUBLIC.LOGIN
      };
    }
    
    // Check subscription requirement
    if (metadata.requiresSubscription && !subscription?.hasActiveSubscription) {
      return {
        valid: false,
        error: ROUTE_ERROR_MESSAGES.SUBSCRIPTION_REQUIRED,
        code: 'SUBSCRIPTION_REQUIRED',
        redirectTo: ROUTE_PATHS.PROTECTED.SUBSCRIPTION
      };
    }
    
    // Check role requirement
    if (metadata.requiredRole && user?.role !== metadata.requiredRole) {
      return {
        valid: false,
        error: ROUTE_ERROR_MESSAGES.INSUFFICIENT_PERMISSIONS,
        code: 'INSUFFICIENT_PERMISSIONS',
        redirectTo: ROUTE_PATHS.PROTECTED.DASHBOARD
      };
    }
    
    return {
      valid: true,
      metadata,
      message: ROUTE_SUCCESS_MESSAGES.PERMISSION_GRANTED
    };
  } catch (error) {
    return {
      valid: false,
      error: ROUTE_ERROR_MESSAGES.INVALID_ROUTE,
      code: 'VALIDATION_ERROR',
      details: error.message
    };
  }
};

// Route Navigation
export const navigateToRoute = (path, options = {}) => {
  try {
    const { replace = false, state = null, user = null, subscription = null } = options;
    
    // Validate route before navigation
    const validation = validateRoute(path, user, subscription);
    
    if (!validation.valid) {
      // Handle validation errors
      if (validation.redirectTo) {
        return navigateToRoute(validation.redirectTo, { replace: true, user, subscription });
      }
      
      throw new Error(validation.error);
    }
    
    // Perform navigation
    if (replace) {
      window.history.replaceState(state, '', path);
    } else {
      window.history.pushState(state, '', path);
    }
    
    // Update document title and meta tags
    updateRouteMeta(path);
    
    return {
      success: true,
      message: ROUTE_SUCCESS_MESSAGES.NAVIGATION_SUCCESS,
      path,
      validation
    };
  } catch (error) {
    console.error('Navigation error:', error);
    
    return {
      success: false,
      error: error.message,
      path
    };
  }
};

// Route Permission Check
export const checkRoutePermission = (pathname, user, subscription) => {
  return validateRoute(pathname, user, subscription);
};

// Breadcrumb Generation
export const generateBreadcrumbs = (pathname) => {
  const pathSegments = pathname.split('/').filter(segment => segment !== '');
  const breadcrumbs = [{ label: 'Начало', path: '/', isActive: pathname === '/' }];

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    let label = segment; // Default label is the segment itself

    // Try to find a more user-friendly label from ROUTE_METADATA
    if (ROUTE_METADATA[currentPath]) {
      label = ROUTE_METADATA[currentPath].title || segment;
      // If title contains " - Арбитражни възможности", extract only the first part
      if (label.includes(' - Арбитражни възможности')) {
        label = label.split(' - Арбитражни възможности')[0];
      }
    }

    breadcrumbs.push({
      label: label,
      path: currentPath,
      isActive: index === pathSegments.length - 1,
    });
  });

  return breadcrumbs;
};

// Default export
export default {
  validateRoute,
  navigateToRoute,
  updateRouteMeta,
  checkRoutePermission,
  generateBreadcrumbs
};